<template>
  <div>
    <page-header :title="$t('bill.modify_exchangerate')">
      <template v-slot:title><currency class="position-absolute" style="right: 0;" /></template>
    </page-header>
    <page-body needMarginBottom>
      <a-alert :message="$t('bill.exchange_rate_exchange_tip')" type="warning" style="margin-bottom: 30px" />
      <a-form-model
        ref="rateForm"
        :model="formModel"
        hide-required-mark>
        <div
          v-for="(item, index) in formModel.rates"
          :key="item.key"
          class="rate-setting-item">
          <div class="rate-symbol">1</div>
          <a-form-model-item
            :prop="`rates.${index}.src`"
            :rules="getSrcRules()"
            class="rate-field">
            <a-select
              v-model="item.src"
              :placeholder="$t('bill.exchange_rate_src')"
              show-search
              option-filter-prop="children">
              <a-select-option
                v-for="option in currencyOptions"
                :key="option.key"
                :value="option.key">
                {{ option.label }} ({{ option.key }})
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <div class="rate-symbol">=</div>
          <a-form-model-item
            :prop="`rates.${index}.rate`"
            :rules="getRateRules()"
            class="rate-field rate-input">
            <a-input-number
              v-model="item.rate"
              :min="0"
              :step="0.0001"
              :precision="6"
              style="width: 100%;" />
          </a-form-model-item>
          <a-form-model-item
            :prop="`rates.${index}.dest`"
            :rules="getDestRules(index)"
            class="rate-field">
            <a-select
              v-model="item.dest"
              :placeholder="$t('bill.exchange_rate_dest')"
              show-search
              option-filter-prop="children">
              <a-select-option
                v-for="option in currencyOptions"
                :key="option.key"
                :value="option.key">
                {{ option.label }} ({{ option.key }})
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <a-button
            v-if="formModel.rates.length > 1"
            class="rate-remove"
            shape="circle"
            icon="minus"
            size="small"
            @click="removeRateItem(index)" />
        </div>
        <a-button
          type="dashed"
          icon="plus"
          class="rate-add-btn"
          @click="addRateItem">
          {{ $t('bill.add_exchange_rate_item') }}
        </a-button>
      </a-form-model>
    </page-body>
    <page-footer>
      <div slot="right">
        <a-button type="primary" class="mr-3" @click="handleConfirm" :loading="loading">{{$t('bill.text_237')}}</a-button>
        <a-button @click="handleCancel">{{$t('common.cancel')}}</a-button>
      </div>
    </page-footer>
  </div>
</template>

<script>
import * as R from 'ramda'
import { mapState } from 'vuex'
import { uuid } from '@/utils/utils'
import { CURRENCY_LIST } from '@/constants/currency'
import { CURRENCYS_RATE_LIST } from '@Bill/constants'
export default {
  name: 'BillExchangeRate',
  components: {
  },
  data () {
    const { status } = this.$route.params || {}
    return {
      loading: false,
      status: status,
      formModel: {
        rates: [],
      },
      deletedRateIds: [],
    }
  },
  computed: {
    ...mapState('common', {
      currencyOpts: state => state.bill.currencyOpts,
    }),
    currencyOptions () {
      return CURRENCY_LIST.map(item => ({ key: item.key, label: item.label || item.key }))
    },
  },
  created () {
    this.$c = new this.$Manager('billing_exchange_rates', 'v1')
    this.fetchData()
  },
  methods: {
    async fetchData () {
      console.log(this.$route.params)
      const { items } = this.$route.params || {}
      this.initRateList(items)
    },
    initRateList (data) {
      const hasData = Array.isArray(data) && data.length
      const sourceList = hasData ? data : R.clone(CURRENCYS_RATE_LIST)
      this.formModel.rates = sourceList.map(item => this.normalizeRateItem(item))
      if (!this.formModel.rates.length) {
        this.addRateItem()
      }
    },
    normalizeRateItem (item = {}) {
      const rest = R.omit(['src', 'dest', 'rate', 'id'], item)
      return {
        key: uuid(),
        src: item.src,
        dest: item.dest,
        rate: item.rate,
        id: item.id,
        ...rest,
      }
    },
    addRateItem () {
      this.formModel.rates.push({
        key: uuid(),
        src: undefined,
        dest: undefined,
        rate: undefined,
      })
    },
    removeRateItem (index) {
      const [removed] = this.formModel.rates.splice(index, 1)
      if (removed && removed.id) {
        this.deletedRateIds.push(removed.id)
      }
      if (!this.formModel.rates.length) {
        this.addRateItem()
      }
    },
    rateValidator (rule, val, callback) {
      if (val && val > 0) {
        callback()
        return
      }
      callback(new Error(this.$t('bill.text_286')))
    },
    getSrcRules () {
      return [
        { required: true, message: this.$t('common.required_cols', [this.$t('bill.exchange_rate_src')]) },
      ]
    },
    getDestRules (index) {
      return [
        { required: true, message: this.$t('common.required_cols', [this.$t('bill.exchange_rate_dest')]) },
        { validator: (rule, value, callback) => this.destValidator(index, value, callback) },
      ]
    },
    getRateRules () {
      return [
        { required: true, message: this.$t('bill.text_286') },
        { validator: this.rateValidator },
      ]
    },
    destValidator (index, value, callback) {
      const current = this.formModel.rates[index]
      if (current && current.src && value && current.src === value) {
        callback(new Error(this.$t('bill.exchange_rate_same_currency')))
        return
      }
      callback()
    },
    buildPayload (items) {
      return items.map(item => ({
        $t: uuid(),
        id: item.id,
        src: item.src,
        dest: item.dest,
        rate: item.rate ? Number(item.rate) : undefined,
      }))
    },
    syncRates ({ createList = [], updateList = [], deleteList = [] }) {
      const reqList = []
      updateList.forEach(item => {
        reqList.push(this.$http({
          method: 'PUT',
          url: `/v1/billing_exchange_rates/${item.id}`,
          data: item,
        }))
      })
      createList.forEach(item => {
        const payload = { ...item, enable_date: this.$route.params.month + '01' }
        delete payload.id
        reqList.push(this.$http({
          method: 'POST',
          url: '/v1/billing_exchange_rates',
          data: payload,
        }))
      })
      deleteList.forEach(id => {
        reqList.push(this.$http({
          method: 'DELETE',
          url: `/v1/billing_exchange_rates/${id}`,
        }))
      })
      if (!reqList.length) return Promise.resolve()
      return Promise.all(reqList)
    },
    async handleConfirm () {
      try {
        this.loading = true
        await this.$refs.rateForm.validate()
        const payload = this.buildPayload(this.formModel.rates)
        const updateList = payload.filter(item => item.id)
        const createList = payload.filter(item => !item.id)
        const deleteList = [...this.deletedRateIds]
        await this.syncRates({ createList, updateList, deleteList })
        this.deletedRateIds = []
        this.$message.success(this.$t('bill.text_59'))
        this.handleCancel()
      } catch (err) {
        throw err
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      this.$router.push({
        path: '/billexchangerate',
      })
    },
  },

}
</script>
<style lang="less" scoped>
.rate-setting-item {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.rate-symbol {
  width: 24px;
  text-align: center;
  font-weight: 500;
  line-height: 32px;
}
.rate-field {
  flex: 0 0 220px;
  margin: 0 8px;
}
.rate-input {
  flex: 0 0 200px;
}
.rate-remove {
  margin-left: 12px;
}
.rate-add-btn {
  margin-left: 32px;
}
</style>
